import { ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { Colors } from 'copyright-clearance-center-common-ui';
import React from 'react';

const useStyles = makeStyles(() =>
	createStyles({
		root: {
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center'
		},
		list: {
			// to override MenuList paddings
			padding: '0 !important',
			'& > li': {
				padding: '0',
				fontWeight: '400',
				fontSize: '0.875rem',
				lineHeight: '2rem',
				color: Colors.neutral700
			},
			'& > hr': {
				// to override default MUI styles
				margin: '0 !important'
			}
		},
		disabled: {
			pointerEvents: 'none'
		}
	})
);

export interface IDropdownButtonProps {
	text?: string;
	iconSvg?: string;
	iconBeforeText?: string;
	className?: string;
	children: React.ReactElement[];
	testId?: string;
	style?: React.CSSProperties;
	disabled?: boolean;
	menuProps?: {
		maxHeight?: string;
		width?: string;
		style?: React.CSSProperties;
		className?: string;
	};
}

const DropdownButton: React.FunctionComponent<IDropdownButtonProps> = props => {
	const classes = useStyles();
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);
	const toggleRef = React.useRef<HTMLButtonElement>(null);

	const handleToggle = () => {
		setOpen(currOpen => !currOpen);
	};

	const handleClose = (event: Event | React.SyntheticEvent) => {
		if (toggleRef.current && toggleRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	function handleListKeyDown(event: React.KeyboardEvent) {
		if (event.key === 'Tab') {
			event.preventDefault();
			setOpen(false);
		} else if (event.key === 'Escape') {
			setOpen(false);
		}
	}

	// return focus to the button when we transitioned from !open -> open
	const prevOpen = React.useRef(open);
	React.useEffect(() => {
		if (prevOpen.current === true && open === false) {
			toggleRef.current.focus();
		}

		prevOpen.current = open;
	}, [open]);

	const btnIcon = props.iconSvg ? <span dangerouslySetInnerHTML={{ __html: props.iconSvg }}></span> : <></>;
	const beforeTextIcon = props.iconBeforeText ? (
		<span dangerouslySetInnerHTML={{ __html: props.iconBeforeText }}></span>
	) : (
		<></>
	);
	const text = props.text ? (
		<span
			style={{
				marginLeft: props.iconBeforeText ? '0.25rem' : '0',
				marginRight: props.iconSvg ? '0.25rem' : '0'
			}}
		>
			{props.text}
		</span>
	) : (
		<></>
	);

	return (
		<div className={clsx('rup-dropdown', classes.root)} ref={anchorRef} style={props.style}>
			<button
				type="button"
				ref={toggleRef}
				data-test-id={props.testId ?? 'dropdown'}
				className={clsx(props.className, {
					['rup-icon-btn']: props.iconSvg && !props.text,
					['rup-btn']: props.text
				})}
				onClick={handleToggle}
				onDoubleClick={e => e.stopPropagation()}
				disabled={props.disabled}
			>
				{beforeTextIcon}
				{text}
				{btnIcon}
			</button>
			<Popper
				open={open}
				anchorEl={anchorRef.current}
				role={undefined}
				style={{ zIndex: '9999' }}
				placement="bottom-start"
				transition
				disablePortal={false}
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom-start' ? 'left top' : 'right top'
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList
									id="composition-menu"
									aria-labelledby="composition-button"
									onKeyDown={handleListKeyDown}
									className={clsx(
										'rup-dropdown__content',
										classes.list,
										{
											[classes.disabled]: !open
										},
										props.menuProps?.className
									)}
									style={{
										maxHeight: props.menuProps?.maxHeight,
										width: props.menuProps?.width,
										...props.menuProps?.style
									}}
								>
									{props.children.map((child, index) => (
										<MenuItem key={index} onClick={handleClose} disableRipple>
											{child}
										</MenuItem>
									))}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</div>
	);
};

export default DropdownButton;
